import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../../components/useTheme';
import { readNextPath, withNext } from '../../lib/nextPath';
import { AuthAside } from './AuthAside';
import { PRIVACY_URL, SITE_URL, TERMS_URL } from './LegalLinks';

const MOON = 'M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z';
const SUN =
  'M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10zM12 1v2M12 21v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M1 12h2M21 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4';

const FOOTER_LINKS = [
  { label: 'Guide', href: `${SITE_URL}/guide.html` },
  { label: 'Terms', href: TERMS_URL },
  { label: 'Privacy', href: PRIVACY_URL },
  { label: 'Contact', href: `${SITE_URL}/contact.html` },
];

const TABS = [
  { key: 'login', label: 'Sign in', path: '/login' },
  { key: 'signup', label: 'Create account', path: '/signup' },
] as const;

interface AuthCardProps {
  title: string;
  description: string;
  children?: ReactNode;
  /** The question line under the form, e.g. "New to Autopostmate? Start a free trial". */
  footer?: ReactNode;
  /** Shows the Sign in / Create account switch with this one selected. */
  tab?: 'login' | 'signup';
  /** A mail tile over the title, for "check your email" states. */
  icon?: 'mail';
}

/** Shared frame for the sign-in, sign-up, password-reset and invite screens:
 *  header, the form column, a site footer, and the checks panel from 980px. */
export function AuthCard({ title, description, children, footer, tab, icon }: AuthCardProps) {
  const { theme, setPreference } = useTheme();
  const location = useLocation();
  const next = readNextPath(location);
  const themeLabel = theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode';

  return (
    <div className="auth-shell">
      <div className="auth-shell__column">
        <header className="auth-shell__header">
          <a className="auth-shell__brand" href={SITE_URL} aria-label="Autopostmate home">
            <span className="auth-shell__mark" aria-hidden="true" />
            <span className="auth-shell__name">Autopostmate</span>
          </a>
          <div className="auth-shell__tools">
            <a className="auth-shell__back" href={SITE_URL}>
              <span aria-hidden="true">←</span>Back to site
            </a>
            <button
              className="auth-shell__theme"
              type="button"
              aria-label={themeLabel}
              title={themeLabel}
              onClick={() => setPreference(theme === 'dark' ? 'light' : 'dark')}
            >
              <svg
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d={theme === 'dark' ? SUN : MOON} />
              </svg>
            </button>
          </div>
        </header>

        <main className="auth-shell__main">
          <div className="auth-card">
            {tab ? (
              <nav className="auth-tabs" aria-label="Account">
                {TABS.map((item) => (
                  <Link
                    key={item.key}
                    className="auth-tabs__tab"
                    to={withNext(item.path, next)}
                    aria-current={item.key === tab ? 'page' : undefined}
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>
            ) : null}

            <div className="auth-card__head">
              {icon === 'mail' ? (
                <span className="auth-card__icon" aria-hidden="true">
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.9"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="5" width="18" height="14" rx="2" />
                    <path d="m3 7 9 6 9-6" />
                  </svg>
                </span>
              ) : null}
              <h1 className="auth-card__title">{title}</h1>
              <p className="auth-card__description">{description}</p>
            </div>

            {children}

            {footer ? <div className="auth-card__question">{footer}</div> : null}
          </div>
        </main>

        <footer className="auth-shell__footer">
          <p>© 2026 Kaizen Global. Autopostmate.</p>
          <nav aria-label="Footer">
            {FOOTER_LINKS.map((link) => (
              <a key={link.label} href={link.href}>
                {link.label}
              </a>
            ))}
          </nav>
        </footer>
      </div>

      <AuthAside />
    </div>
  );
}
